// Prompt 16 — Verbatim legal copy. Do not edit, summarise, or reword.
// Rendered by `components/LegalPage.jsx` (markdown + GFM, mailto autolink,
// HTML comments stripped).
import { PRICE_LABEL } from "./pricing";
import { PLATFORMS, publicPlatforms, livePlatforms, comingSoon, joinList } from "../lib/platforms";

const LIVE = livePlatforms();
const SOON = comingSoon();
const BULLETS = publicPlatforms().map((p) => `- ${p.name}${p.status === "coming_soon" ? " (planned, not yet available)" : p.status === "beta" ? " (beta)" : ""}`).join("\n");
const VENDORS_OR = joinList([...new Set(LIVE.map((p) => p.vendor))], "or", true);
const SUPPORTED_SENTENCE = `Supported systems are ${joinList(LIVE.map((p) => p.name), "and", true)}.${SOON.length ? ` ${joinList(SOON.map((p) => p.name))} support is planned.` : ""}`;
const SHORT_OR = joinList(LIVE.map((p) => p.short), "or", true);
const TRADEMARKS = joinList(publicPlatforms().map((p) => p.name), "and", true);
const REVOKE = {
  quickbooks: "- **QuickBooks Online**: Sign in to QuickBooks Online → Apps → My Apps → find Lingua Franca → Disconnect. You can also disconnect from inside LF, which uses Intuit's disconnect flow.",
  xero: "- **Xero**: Sign in to Xero → organization name → Settings → Connected apps → Lingua Franca → Disconnect.",
  zoho: "- **Zoho Books**: Sign in to your Zoho account → accounts.zoho.com → Connected Apps (or Sessions → Connected Apps) → Lingua Franca → Revoke.",
  bc: "- **Microsoft Dynamics 365 Business Central**: A Microsoft Entra ID (Azure AD) administrator can remove access in the Microsoft Entra admin center → Enterprise applications → Lingua Franca → Delete or revoke permissions. Individual users can review app access at myapps.microsoft.com / My Account → App permissions.",
  freshbooks: "- **FreshBooks**: Sign in to FreshBooks → Settings → Apps → Connected Apps → Lingua Franca → Disconnect.",
  odoo: "- **Odoo**: In Odoo, open your user → Account Security → API Keys and delete the key you created for Lingua Franca.",
};
const REVOKE_LINES = PLATFORMS.filter((p) => p.status !== "hidden" && REVOKE[p.key]).map((p) => REVOKE[p.key]).join("\n");

export const TERMS = `# Lingua Franca Terms of Service

Effective date: October 15, 2026

Last updated: October 16, 2026

These Terms of Service ("Terms") are an agreement between you and Lingua Franca LLC ("Lingua Franca," "LF," "we," "us," or "our"). They cover your use of the Lingua Franca website at https://linguafrancainv.com and the Lingua Franca invoice clearinghouse service (together, the "Service").

If you use the Service for a business, you confirm that you have authority to accept these Terms for that business. In that case "you" means both you and the business. If you do not agree to these Terms, do not use the Service.

Our Privacy Policy explains how we handle your data. It is part of these Terms.

---

## 1. What Lingua Franca does

Lingua Franca moves structured invoice data between accounting systems. When an invoice is issued in one connected accounting system, LF can deliver it as a bill or invoice record to another business's connected accounting system. Supported systems are:

${BULLETS}

Each of these is a "Connected System" or "GL" (general ledger).

What LF does not do:

- LF does not create invoices. Invoices start in an accounting system that you or your trading partner control. LF only carries their data across.
- LF does not process, send, hold, or move money. LF is not a payment processor, bank, or money transmitter.
- Payment status is read-only. LF may show whether an invoice has been marked paid. It reads that status from the receiving business's accounting system and cannot change it.
- LF does not give accounting, tax, legal, or financial advice (see Section 8).

## 2. Accounts

- You must give accurate information when you create an account and keep it up to date.
- You are responsible for keeping your login details safe and for everything that happens under your account. Tell us right away at support@linguafrancainv.com if you think someone has accessed your account without permission.
- The Service is for businesses and the people who work for them. You must be at least 18 years old and able to enter a binding contract.
- One person or business may not create accounts to get around limits, suspensions, or fees.

## 3. Connecting your accounting systems

- You connect a Connected System by signing in to that system and approving access through its own authorization screen (OAuth). LF never sees or stores your accounting system password.
- You may only connect accounting systems, companies, or organizations that you are authorized to connect.
- By connecting a system, you authorize LF to read and write the data needed to run the Service. This includes invoices, bills, contacts, items, accounts, and payment status, as described in our Privacy Policy.
- You can disconnect a Connected System at any time (see Section 10 and the Contact & Support page). After you disconnect, LF stops syncing with that system and revokes or deletes its access tokens.
- Your use of each Connected System is also governed by that provider's own terms. LF is not affiliated with or endorsed by ${VENDORS_OR} unless we state otherwise. Their names and products are trademarks of their respective owners.

## 4. Accuracy, mappings, and your responsibility to review

LF is a pipe. It moves the data it receives, in the way you configure it.

- You are responsible for your mappings. You choose how items, products, and services map to accounts, tax codes, and other fields in the receiving accounting system. Review those mappings before you rely on them, and check them again after any change to your chart of accounts.
- You are responsible for reviewing synced records. Check bills and invoices that LF delivers before you approve, post, or pay them.
- LF does not guarantee accounting correctness. We work to deliver data accurately and reliably. However, we do not guarantee that any record is complete, correctly coded, correctly taxed, or fit for your financial statements, tax filings, or audits. Errors can come from source data, mappings, Connected System behavior, API limits, or outages.
- Your books are your responsibility. You and your accountant are responsible for your records, tax compliance, and financial reporting.

## 5. Acceptable use

You agree not to:

- Use the Service for anything illegal, fraudulent, or deceptive. This includes sending fake or inflated invoices, invoice fraud, money laundering, or evading sanctions or taxes.
- Send invoices or data to a business that has not agreed to receive them through LF, or use the Service to send spam.
- Connect accounting systems or companies you are not authorized to access.
- Upload or send malware, or data that infringes someone else's rights.
- Try to access other customers' data, or probe, scan, or test the Service's security without our written permission.
- Interfere with or overload the Service or any Connected System, including by getting around rate limits.
- Reverse engineer, copy, resell, or build a competing product from the Service, except where the law clearly allows it.
- Scrape the Service, or access it through automated means other than the interfaces we provide.
- Use the Service in a way that breaks the terms of a Connected System provider.

We may investigate suspected violations. We may also suspend access (see Section 10) to protect the Service, our customers, or the Connected Systems.

## 6. Your data

- You own your data. As between you and LF, you keep all rights to the invoice and accounting data you or your Connected Systems provide ("Customer Data").
- Limited license to us. You give LF a limited license to host, process, transmit, and display Customer Data only as needed to provide, secure, support, and improve the reliability of the Service, and as described in the Privacy Policy.
- No advertising, no sale, no AI training. LF does not sell Customer Data. It does not use Customer Data or data from Connected Systems for advertising. It does not use that data to train machine learning or artificial intelligence models.
- Your trading partners. When you send an invoice through LF, its data goes to the receiving business's Connected System, as you directed. After delivery, that business controls its copy under its own policies.

## 7. Fees and billing

- Who pays. The receiving business (the LF customer whose Receiving GL receives the synced document) pays for the Service. Senders are not charged for sending invoices through LF.
- Free period. Free for 6 months or 100 received invoices from signup, whichever comes first. Then $0.25 per received invoice, charged once your balance reaches $5. No card needed to start.
- Paid rate. After the free period ends, the receiving business is charged US ${PRICE_LABEL} for each invoice that successfully syncs into its Receiving GL for the first time. There is no monthly fee or minimum. Retries, idempotent re-syncs, and payment-status refreshes on an already-synced invoice are not charged again.
- What counts as a sync. An invoice counts when LF first stores a document id returned by the Receiving GL for that source invoice. Held, blocked, failed, or needs-mapping invoices are not charged.
- Fees are collected through a third-party payment processor once paid billing is turned on. Until then, usage may be recorded without charging a card. LF itself does not store full payment card numbers.
- Fees do not include taxes. You are responsible for any applicable sales, use, or similar taxes.
- Unless the law requires otherwise or we state otherwise, fees are non-refundable.
- We may change prices with at least 30 days' notice. The change takes effect after that notice period for future syncs.

## 8. No financial, tax, or legal advice

Nothing in the Service, our documentation, or our support communications is accounting, tax, legal, or financial advice. Suggested mappings, defaults, or examples are for convenience only. Talk to a qualified professional about your own situation.

## 9. Service availability and changes

We aim to keep the Service available. We do not promise it will be uninterrupted or error-free. Connected Systems may change their APIs, limit access, or go down, and that can affect the Service.

We may add, change, or remove features. If we remove a significant feature you use, we will try to give reasonable advance notice.

We do not offer a service-level agreement or uptime guarantee.

## 10. Suspension and termination

By you: You may stop using the Service and close your account at any time. Instructions are on the Contact & Support page. Closing your account does not by itself refund prepaid fees, except as stated in Section 7.

By us: We may suspend or end your access if you materially breach these Terms, fail to pay fees, create a security or legal risk, or if a Connected System provider requires it. When it is reasonable to do so, we will give notice first and a chance to fix the problem.

What happens after termination: Syncing stops and we revoke access tokens for your Connected Systems. We then delete or return Customer Data as described in the Privacy Policy. Sections 4, 6 (ownership), 8, 11, 12, 13, 14, 15, and 17 continue to apply after termination.

## 11. Disclaimers

THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." TO THE FULLEST EXTENT ALLOWED BY LAW, LF DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS OR IMPLIED. THIS INCLUDES WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, AND ACCURACY. LF DOES NOT WARRANT THAT DATA DELIVERED THROUGH THE SERVICE WILL BE ACCURATE, COMPLETE, OR CORRECTLY CODED. LF IS NOT RESPONSIBLE FOR THE ACTS, OMISSIONS, OR AVAILABILITY OF CONNECTED SYSTEMS OR YOUR TRADING PARTNERS.

## 12. Indemnity

You will defend and indemnify LF against claims, losses, and reasonable costs (including reasonable attorneys' fees) that arise from (a) your Customer Data, (b) invoices you send through the Service, (c) your breach of these Terms, or (d) your violation of law or of a third party's rights.

## 13. Limitation of liability

TO THE FULLEST EXTENT ALLOWED BY LAW:

LF WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. LF WILL ALSO NOT BE LIABLE FOR LOST PROFITS, REVENUE, OR DATA; LATE-PAYMENT PENALTIES; DUPLICATE OR MISSED PAYMENTS; TAX PENALTIES; OR ACCOUNTING ERRORS. THIS APPLIES EVEN IF LF WAS TOLD THESE DAMAGES WERE POSSIBLE.

LF'S TOTAL LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE IS LIMITED TO THE GREATER OF (A) THE AMOUNT YOU PAID LF IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR (B) US $100.

Some jurisdictions do not allow some of these limits. In those places, the limits apply only as far as the law allows.

## 14. Governing law and venue

These Terms are governed by the laws of the State of Minnesota, not counting its conflict-of-law rules. Except as provided in Section 15, any dispute will be heard only in the state or federal courts located in Steele County, Minnesota. You and LF both consent to those courts' jurisdiction.

## 15. Dispute resolution

Before filing a claim, each party agrees to try to resolve the dispute informally. To start, contact the other party in writing and allow 30 days for a response.

## 16. Changes to these Terms

We may update these Terms from time to time. If a change is material, we will notify you by email or in the Service at least 30 days before it takes effect, unless the change is required by law or for security reasons. The "Last updated" date at the top shows when the Terms last changed. If you keep using the Service after a change takes effect, you accept the updated Terms.

## 17. General

- Entire agreement: These Terms and the Privacy Policy are the entire agreement between you and LF about the Service.
- Assignment: You may not transfer these Terms without our consent. We may transfer them as part of a merger, acquisition, or sale of assets, with notice to you.
- Severability: If any part of these Terms cannot be enforced, the rest still applies.
- No waiver: If we do not enforce a right, we have not given it up.
- Force majeure: Neither party is liable for delays caused by events beyond its reasonable control, including Connected System outages.
- Notices: We will send notices to the email on your account. Send notices to us at the address in Section 18.

## 18. Contact

Lingua Franca LLC, a Minnesota limited liability company

Email: support@linguafrancainv.com

Billing: billing@linguafrancainv.com

Mailing address: Lingua Franca LLC, c/o Northwest Registered Agent LLC, 202 N Cedar Ave, STE #1, Owatonna, MN 55060

Website: https://linguafrancainv.com
`;

export const PRIVACY = `# Lingua Franca Privacy Policy

Effective date: October 15, 2026

Last updated: October 16, 2026

This Privacy Policy explains how Lingua Franca LLC ("Lingua Franca," "LF," "we," "us," or "our") collects, uses, shares, protects, and deletes information when you use https://linguafrancainv.com and the Lingua Franca invoice clearinghouse service (the "Service").

The short version:

- We use your data only to move invoice data between the accounting systems you connect, and to run, secure, and support the Service.
- We do not sell your data.
- We do not use your accounting data for advertising.
- We do not use your accounting data to train machine learning or AI models.
- We share data only with the accounting systems you tell us to send it to, and with the infrastructure providers that help us run the Service.
- You can disconnect an accounting system at any time, and you can ask us to delete your data.

---

## 1. Who this policy covers

This policy covers:

- Customers: businesses and their users who sign up for LF and connect an accounting system.
- Trading partners: vendors and customers whose names and email addresses appear on invoices that pass through LF.
- Website visitors: people who visit https://linguafrancainv.com.

## 2. Information we collect

### a. Account and billing information

- Your name, business name, and login email address.
- Billing contact email addresses.
- Subscription and billing records. Payments for LF subscriptions are handled by a third-party payment processor. LF does not store full card numbers.

### b. Accounting system connection data

You connect an accounting system by approving access on that provider's own sign-in screen (OAuth). ${SUPPORTED_SENTENCE} We store:

- OAuth access and refresh tokens. These are encrypted at rest. We never receive or store your accounting system password.
- Identifiers for the connected company, organization, or tenant (for example, a company or realm ID).

### c. Invoice and accounting data

To move invoices between systems, we access and store:

- Vendor and customer names and email addresses.
- Invoice and bill details: invoice numbers, dates, due dates, line items, descriptions, quantities, prices, amounts, taxes, and currency.
- Item-to-account mappings and other settings you configure. These are the rules that decide how an item on an incoming invoice is recorded in the receiving system's chart of accounts.
- Reference data from the connected system that is needed for mapping, such as lists of accounts, items, tax codes, and contacts.
- Payment status (read-only). We read whether an invoice or bill has been marked paid in the receiving accounting system. LF never creates invoices, never processes payments, and never changes payment records.

### d. Sync history and logs

- Records of each sync: what was sent, when, to which connected system, and whether it succeeded or failed (including error messages).
- Security and operational logs, such as sign-in events, IP addresses, browser and device type, and API request logs.

### e. Website information

Basic usage information from our website, such as pages visited, referring site, and approximate location based on IP address. We do not use advertising cookies or sell this information.<!-- TODO: name any analytics tool if one is added -->

We use cookies that are strictly necessary to keep you signed in and secure. We do not use advertising cookies.

### f. Information you send us

Messages you send to support, and any attachments or details you include.

## 3. How we use information

We use information only to:

- Provide the Service. We read invoices from the sending system, apply your mappings, and deliver the records to the receiving system you chose.
- Show you sync status, history, errors, and read-only payment status.
- Send service emails. These include confirmations, sync alerts, error notices, security notices, and billing emails. We send them through our email delivery provider.
- Keep the Service secure, prevent fraud and abuse, and troubleshoot problems.
- Provide customer support.
- Bill for the Service and keep required business records.
- Comply with the law and with the terms of the accounting platforms we connect to.

What we do not do:

- We do not sell or rent personal information or accounting data.
- We do not use data from your accounting systems for advertising or marketing profiles.
- We do not use data from your accounting systems to train machine learning or AI models, whether ours or anyone else's.
- We do not access your accounting data except as needed to run, secure, or support the Service, or when you ask us to (for example, to investigate a support ticket).

We may use aggregated, de-identified operational metrics, such as total number of syncs or error rates, to monitor and improve reliability. These metrics do not include invoice contents and cannot reasonably be used to identify you or your trading partners.

## 4. How we share information

We share information only in these situations:

- With the accounting systems you connect, at your direction. When you send an invoice through LF, its data goes to the receiving business's connected accounting system. After that, the receiving business and its accounting provider handle that data under their own policies.
- With infrastructure providers (subprocessors) who help us run the Service. They may process data only on our instructions and only to provide their services to us. They fall into these categories:
  - Cloud hosting and database providers
  - Email delivery provider for confirmation and alert emails
  - Payment and billing provider for LF subscriptions
  - Error monitoring and logging providers

<!-- TODO: list actual subprocessor names (hosting, email e.g. Resend, billing, monitoring) once finalized, or state that a list is available on request -->

Contact us for a current list of subprocessors.

- For legal reasons. We may share information if we are required to by law, subpoena, or court order. We may also share it when needed to protect the rights, safety, or security of LF, our customers, or others. When the law allows, we will tell you before we disclose your data.
- Business transfers. If LF is involved in a merger, acquisition, or sale of assets, information may be transferred as part of that deal. It will remain protected by this policy, or we will notify you before it becomes subject to a different policy.

We do not share data with data brokers, advertisers, or marketing partners.

## 5. Security

We use reasonable administrative, technical, and physical safeguards to protect your information, including:

- Encryption of data in transit (TLS/HTTPS).
- Encryption of OAuth tokens at rest.
- Access limited to the people who need it to operate and support the Service.
- OAuth access with only the permissions needed for invoice sync. We never handle accounting system passwords.
- Logging and monitoring of access to production systems.

<!-- TODO: add database-at-rest encryption and admin MFA statements only once verified -->

No method of transmission or storage is completely secure, and we cannot guarantee absolute security. LF does not currently hold third-party security certifications (such as SOC 2 or ISO 27001).

## 6. Breach notification

If we become aware of unauthorized access to your data that affects its security or confidentiality, we will:

- Notify affected customers without undue delay, and in any event within 72 hours of confirming the incident, by email to the account and billing contacts we have on file.
- Notify affected accounting platform providers (for example, ${VENDORS_OR}) as their developer terms require.
- Tell you what happened, what data was involved, what we are doing about it, and what you can do.
- Notify regulators or individuals as required by applicable law.

## 7. Data retention

We keep information only as long as we need it for the purposes in this policy:

| Data | How long we keep it |
| --- | --- |
| OAuth tokens | Until you disconnect the accounting system or close your account. Then they are revoked and deleted. |
| Invoice data and mappings | While your account is active. Deleted within 30 days after you close your account or request deletion. |
| Sync history and error logs | 12 months on a rolling basis, or until account deletion, whichever comes first. |
| Security and operational logs | Up to 90 days. |
| Billing records | As long as the law requires for tax and accounting purposes (typically 7 years). |
| Backups | Deleted data is removed from backups within 90 days as the backups cycle out. |
| Support messages | Up to 24 months after the ticket is closed. |

## 8. Your choices and rights

### Disconnect an accounting system

You can disconnect any connected accounting system at any time:

- From LF: Sign in at https://linguafrancainv.com, go to Settings → Connections, and click Disconnect next to the system. LF stops syncing right away and revokes and deletes the OAuth tokens for that connection.
- From the accounting system: You can also revoke LF's access from inside your accounting system. Instructions for each system are on our Contact & Support page.

Disconnecting does not delete your invoice data or sync history in LF. To remove those, request deletion as described below. Disconnecting also does not delete records that LF already delivered into a connected accounting system. Those belong to the owner of that system.

### Request deletion of your data

Email privacy@linguafrancainv.com from your account email with the subject "Data deletion request". We will:

- Verify the request comes from an authorized user of the account.
- Disconnect any remaining accounting systems and revoke their tokens.
- Delete your invoice data, mappings, and sync history within 30 days, except for billing records and other data we are legally required to keep.
- Confirm by email when deletion is complete.

### Other requests

You may ask to access, correct, or export your data. Contact us at privacy@linguafrancainv.com. Depending on where you live, you may have additional rights under local privacy laws. We will not discriminate against you for exercising your rights.

### Trading partners

If your name or email address appears on an invoice sent through LF and you want to make a request, contact us. We may need to coordinate with the LF customer who sent or received that invoice.

### Emails

Service emails, such as sync alerts and security or billing notices, are part of the Service. You can adjust optional alerts in your account's notification settings. If we ever send marketing emails, every one will include an unsubscribe link.

## 9. Children

The Service is for businesses. It is not directed to children under 16, and we do not knowingly collect their information.

## 10. International users

LF is based in the United States, and data is stored and processed in the United States. If you use the Service from outside the US, your information will be transferred to and processed in the United States.<!-- TODO: add transfer safeguards if EU/UK customers are served -->

## 11. Changes to this policy

We may update this policy from time to time. If a change is material, we will notify you by email or in the Service at least 30 days before it takes effect. The "Last updated" date at the top shows the latest version.

## 12. Contact us

Lingua Franca LLC, a Minnesota limited liability company

Privacy and data requests: privacy@linguafrancainv.com

Mailing address: Lingua Franca LLC, c/o Northwest Registered Agent LLC, 202 N Cedar Ave, STE #1, Owatonna, MN 55060

Website: https://linguafrancainv.com
`;

export const CONTACT = `# Contact & Support

We're here to help with setup, mappings, sync errors, billing, and privacy questions.

## How to reach us

| Topic | Contact |
| --- | --- |
| General support | support@linguafrancainv.com |
| Billing questions | billing@linguafrancainv.com |
| Privacy and data deletion requests | privacy@linguafrancainv.com |
| Security issues or suspected unauthorized access | security@linguafrancainv.com (subject line: "SECURITY") |

Mailing address: Lingua Franca LLC, c/o Northwest Registered Agent LLC, 202 N Cedar Ave, STE #1, Owatonna, MN 55060

Website: https://linguafrancainv.com

Support hours: Monday–Friday, 9 a.m.–5 p.m. US Central, excluding US holidays

Response time: We'll respond as soon as we can.

To help us resolve your issue quickly, please include:

- The email address on your LF account
- Which accounting system(s) are involved (${SHORT_OR})
- The invoice number(s) and approximate date and time of the sync
- Any error message you saw (a screenshot helps)

Please do not email passwords, OAuth tokens, or full bank or card numbers. We will never ask for your accounting system password.

---

## Common questions

### Does Lingua Franca create invoices or take payments?

No. LF only moves invoice data that already exists in a connected accounting system. LF never creates invoices and never processes payments. Payment status shown in LF is read-only, taken from the receiving business's accounting system.

### An invoice landed in the wrong account. What happened?

LF records items using the item-to-account mappings set up for your connection. Check your mappings at Settings → Mappings, and review delivered bills in your accounting system before you approve or pay them. LF moves data as configured, but it does not guarantee accounting correctness. See our Terms of Service.

### Can you give me accounting or tax advice?

We can help you use LF. For advice on how to code transactions or handle tax, please talk to your accountant.

### Does LF use my data for ads or AI training?

No. See our Privacy Policy.

---

## How to disconnect an accounting system

Disconnecting stops all syncing with that system right away. Records LF already delivered stay in the accounting system.

### Option 1: Disconnect from Lingua Franca (recommended)

1. Sign in at https://linguafrancainv.com.
2. Open your account's Settings and find the Connections section.<!-- TODO: confirm exact UI path once built -->
3. Click Disconnect next to the accounting system.
4. Confirm. LF revokes the OAuth token with the provider and deletes it from our systems.

### Option 2: Revoke access from your accounting system

You can also remove LF's access directly from your accounting provider.<!-- TODO: verify each vendor path below against current vendor UI before marketplace submission -->

${REVOKE_LINES}

If you revoke access from the accounting system side, LF will mark the connection as disconnected and stop syncing. To remove the stored data as well, request deletion (below).

---

## How to request deletion of your data

1. Email privacy@linguafrancainv.com from the email address on your LF account, with the subject "Data deletion request."
2. We will verify the request and disconnect any remaining accounting systems.
3. We will delete your invoice data, mappings, and sync history within 30 days. Billing records we are legally required to keep are the only exception.
4. We will email you to confirm when deletion is complete.

Full details are in our Privacy Policy.

---

## Reporting a security issue

If you believe your account or data has been accessed without permission, or you have found a security vulnerability, email security@linguafrancainv.com with "SECURITY" in the subject line. Please give us a reasonable chance to fix the issue before you share it publicly. Do not access or change other customers' data while investigating.

---

## Legal

[Terms of Service](/terms)

[Privacy Policy](/privacy)

Lingua Franca is operated by Lingua Franca LLC, a Minnesota limited liability company. ${TRADEMARKS} are trademarks of their respective owners. Lingua Franca is not affiliated with or endorsed by them unless stated otherwise.
`;
